import type { Trade } from '../sim/types'

// Pure statistics over an array of closed trades. No persistence; the caller
// hands in a (possibly filtered) trade list + the account's starting balance.

export interface EquityPoint {
  time: number
  equity: number
  tradeId: string
}

export interface DrawdownResult {
  maxDrawdown: number
  maxDrawdownPct: number
  durationTrades: number
}

export interface StatsResult {
  totalTrades: number
  wins: number
  losses: number
  breakeven: number
  netPnL: number
  grossProfit: number
  grossLoss: number
  totalCommission: number
  winRate: number // 0..1
  profitFactor: number | null // null if no losses
  expectancy: number // $ per trade
  expectancyR: number | null // mean R (trades with R only)
  avgWin: number
  avgLoss: number
  avgWinLossRatio: number | null
  largestWin: number
  largestLoss: number
  maxDrawdown: number
  maxDrawdownPct: number
  maxWinStreak: number
  maxLossStreak: number
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function buildEquityCurve(trades: Trade[], startingBalance: number): EquityPoint[] {
  const sorted = [...trades].sort((a, b) => a.exitTime - b.exitTime)
  const out: EquityPoint[] = []
  let bal = startingBalance
  for (const t of sorted) {
    bal = round2(bal + t.netPnL)
    out.push({ time: t.exitTime, equity: bal, tradeId: t.id })
  }
  return out
}

export function maxDrawdown(curve: EquityPoint[], startingBalance: number): DrawdownResult {
  let peak = startingBalance
  let peakIdx = -1
  let maxDD = 0
  let maxDDpct = 0
  let worstStart = -1
  let worstEnd = -1
  for (let i = 0; i < curve.length; i++) {
    const eq = curve[i]!.equity
    if (eq > peak) {
      peak = eq
      peakIdx = i
    }
    const dd = peak - eq
    if (dd > maxDD) {
      maxDD = dd
      maxDDpct = peak > 0 ? dd / peak : 0
      worstStart = peakIdx
      worstEnd = i
    }
  }
  return {
    maxDrawdown: round2(maxDD),
    maxDrawdownPct: round2(maxDDpct * 100), // as a percentage, e.g. 4.55
    durationTrades: worstEnd >= 0 ? worstEnd - worstStart : 0,
  }
}

export function computeStats(trades: Trade[], startingBalance: number): StatsResult {
  const n = trades.length
  let wins = 0
  let losses = 0
  let breakeven = 0
  let grossProfit = 0
  let grossLoss = 0
  let totalCommission = 0
  let largestWin = 0
  let largestLoss = 0
  let netPnL = 0
  let rSum = 0
  let rCount = 0
  let maxWinStreak = 0
  let maxLossStreak = 0
  let curWin = 0
  let curLoss = 0

  for (const t of trades) {
    netPnL += t.netPnL
    totalCommission += t.commission
    if (t.netPnL > 0) {
      wins++
      grossProfit += t.netPnL
      if (t.netPnL > largestWin) largestWin = t.netPnL
      curWin++
      curLoss = 0
      if (curWin > maxWinStreak) maxWinStreak = curWin
    } else if (t.netPnL < 0) {
      losses++
      grossLoss += t.netPnL // negative
      if (t.netPnL < largestLoss) largestLoss = t.netPnL
      curLoss++
      curWin = 0
      if (curLoss > maxLossStreak) maxLossStreak = curLoss
    } else {
      breakeven++
      curWin = 0
      curLoss = 0
    }
    if (t.rMultiple !== null) {
      rSum += t.rMultiple
      rCount++
    }
  }

  const curve = buildEquityCurve(trades, startingBalance)
  const dd = maxDrawdown(curve, startingBalance)

  return {
    totalTrades: n,
    wins,
    losses,
    breakeven,
    netPnL: round2(netPnL),
    grossProfit: round2(grossProfit),
    grossLoss: round2(grossLoss),
    totalCommission: round2(totalCommission),
    winRate: wins + losses > 0 ? wins / (wins + losses) : 0,
    profitFactor: grossLoss !== 0 ? round2(grossProfit / Math.abs(grossLoss)) : null,
    expectancy: n > 0 ? round2(netPnL / n) : 0,
    expectancyR: rCount > 0 ? round2(rSum / rCount) : null,
    avgWin: wins > 0 ? round2(grossProfit / wins) : 0,
    avgLoss: losses > 0 ? round2(grossLoss / losses) : 0,
    avgWinLossRatio:
      wins > 0 && losses > 0 ? round2(grossProfit / wins / Math.abs(grossLoss / losses)) : null,
    largestWin: round2(largestWin),
    largestLoss: round2(largestLoss),
    maxDrawdown: dd.maxDrawdown,
    maxDrawdownPct: dd.maxDrawdownPct,
    maxWinStreak,
    maxLossStreak,
  }
}
